import React, { useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CHRONOS_CANVAS_BG } from "./chronos/chronosConstants";

/**
 * Overview strip: full data extent vs current view.
 * Click empty track to jump; drag the thumb for 1:1 cursor tracking (like a native scrollbar).
 */
export default function ChronosMinimap({
  dataMinT,
  dataMaxT,
  viewStart,
  viewEnd,
  onPanByDataMs,
  /** Set view start (ms); end follows current span. Required for thumb drag (1:1 with cursor; avoids clamp vs. delta mismatch). */
  onSeekViewStartMs,
}) {
  const trackRef = useRef(null);
  /** Thumb drag: grab offset within track (px from track left to thumb left at pointerdown) */
  const thumbDragRef = useRef(null);
  /** Suppress track jump click after a thumb drag */
  const suppressTrackClickRef = useRef(false);

  const spanData = Math.max(1, dataMaxT - dataMinT);
  const v0 = viewStart.getTime();
  const v1 = viewEnd.getTime();
  const spanView = Math.max(1, v1 - v0);

  const leftFrac = Math.max(0, Math.min(1, (v0 - dataMinT) / spanData));
  const widthFrac = Math.max(0.02, Math.min(1, spanView / spanData));

  const onTrackClick = useCallback(
    (e) => {
      if (e.target !== e.currentTarget) return;
      if (suppressTrackClickRef.current) {
        suppressTrackClickRef.current = false;
        return;
      }
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const frac = Math.max(0, Math.min(1, x / rect.width));
      const half = widthFrac / 2;
      const targetLeft = Math.max(0, Math.min(1 - widthFrac, frac - half));
      const targetV0 = dataMinT + targetLeft * spanData;
      onPanByDataMs(targetV0 - v0);
    },
    [dataMinT, spanData, v0, widthFrac, onPanByDataMs]
  );

  const onThumbPointerDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const trackW = rect.width;
      if (trackW <= 0) return;
      const thumbLeftPx = leftFrac * trackW;
      const mx = e.clientX - rect.left;
      /** Offset from pointer to thumb’s left edge — keeps grab point under cursor while dragging */
      const grabOffsetPx = mx - thumbLeftPx;
      thumbDragRef.current = {
        grabOffsetPx,
        lastClientX: e.clientX,
        movedPx: 0,
      };
      track.setPointerCapture(e.pointerId);
    },
    [leftFrac]
  );

  const onTrackPointerMove = useCallback(
    (e) => {
      const d = thumbDragRef.current;
      if (!d) return;
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const trackW = rect.width;
      if (trackW <= 0) return;
      d.movedPx += Math.abs(e.clientX - d.lastClientX);
      d.lastClientX = e.clientX;
      const thumbW = widthFrac * trackW;
      const movable = Math.max(0, trackW - thumbW);
      const mx = e.clientX - rect.left;
      let thumbLeftPx = mx - d.grabOffsetPx;
      thumbLeftPx = Math.max(0, Math.min(movable, thumbLeftPx));
      const rangeT = spanData - spanView;
      if (rangeT <= 1e-9) return;
      const targetV0 =
        dataMinT + (movable > 0 ? (thumbLeftPx / movable) * rangeT : 0);
      onSeekViewStartMs(targetV0);
    },
    [spanData, spanView, dataMinT, widthFrac, onSeekViewStartMs]
  );

  const endThumbDrag = useCallback((e) => {
    const d = thumbDragRef.current;
    if (!d) return;
    thumbDragRef.current = null;
    if (d.movedPx > 4) {
      suppressTrackClickRef.current = true;
    }
    try {
      trackRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <Box
      sx={{
        height: 32,
        width: "100%",
        minWidth: 0,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: alpha(CHRONOS_CANVAS_BG, 0.55),
        px: 1,
        py: 0.5,
        boxSizing: "border-box",
      }}
    >
      <Box
        ref={trackRef}
        onClick={onTrackClick}
        onPointerMove={onTrackPointerMove}
        onPointerUp={endThumbDrag}
        onPointerCancel={endThumbDrag}
        role="presentation"
        sx={{
          position: "relative",
          height: "100%",
          borderRadius: 1,
          bgcolor: "rgba(200, 200, 210, 0.18)",
          cursor: "pointer",
          touchAction: "none",
        }}
      >
        <Box
          onPointerDown={onThumbPointerDown}
          sx={{
            position: "absolute",
            left: `${leftFrac * 100}%`,
            width: `${widthFrac * 100}%`,
            top: 0,
            bottom: 0,
            borderRadius: 0.5,
            border: "2px solid",
            borderColor: "text.primary",
            bgcolor: alpha(CHRONOS_CANVAS_BG, 0.85),
            boxSizing: "border-box",
            cursor: "grab",
            touchAction: "none",
            "&:active": { cursor: "grabbing" },
          }}
        />
      </Box>
    </Box>
  );
}
